import React from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, Check, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";

const STORAGE_KEY = "pr-cookie-consent-v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...value, ts: new Date().toISOString() }));
  } catch {
    /* ignore */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [prefs, setPrefs] = React.useState({ essential: true, analytics: false, marketing: false });

  React.useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    writeConsent({ essential: true, analytics: true, marketing: true, choice: "accept_all" });
    setVisible(false);
  };
  const reject = () => {
    writeConsent({ essential: true, analytics: false, marketing: false, choice: "reject_non_essential" });
    setVisible(false);
  };
  const savePrefs = () => {
    writeConsent({ ...prefs, choice: "custom" });
    setOpen(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="cookie-banner"
        className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[440px] z-[60] rounded-xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/10 p-5"
      >
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700">
            <Cookie className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h4 className="font-display text-base font-semibold text-slate-900">Cookies on PlayerReach</h4>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              We use essential cookies to operate this site. With your consent, we may use additional
              cookies for analytics and marketing. Read our{" "}
              <Link to="/legal/cookie-policy" className="text-[#0052FF] hover:underline">Cookie Policy</Link>.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={accept} data-testid="cookie-accept-all" className="btn-primary text-sm px-4 py-2">
                <Check className="h-4 w-4" /> Accept all
              </button>
              <button onClick={reject} data-testid="cookie-reject" className="btn-secondary text-sm px-4 py-2">
                <X className="h-4 w-4" /> Reject non-essential
              </button>
              <button onClick={() => setOpen(true)} data-testid="cookie-manage" className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-2">
                <Settings2 className="h-4 w-4" /> Manage
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="cookie-preferences-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Cookie preferences</DialogTitle>
            <DialogDescription className="text-slate-600">
              Choose which categories of cookies you allow on this site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <PrefRow title="Essential" desc="Required for the site to function. Cannot be disabled." checked disabled />
            <PrefRow
              title="Analytics"
              desc="Helps us understand site usage and improve content."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              testId="pref-analytics"
            />
            <PrefRow
              title="Marketing"
              desc="Used to measure campaign performance and audience interest."
              checked={prefs.marketing}
              onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
              testId="pref-marketing"
            />
          </div>
          <DialogFooter>
            <button onClick={savePrefs} data-testid="cookie-save-prefs" className="btn-primary text-sm">
              Save preferences
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 p-3 rounded-md border border-slate-200">
      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
    </div>
  );
}
